import { CartesianGrid, Cell, Legend, Line, LineChart, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import { axisDateLabel, pointChangeLabel, tooltipDateLabel } from '@/lib/chartDates';

const tooltipStyle = {
	background: 'hsl(var(--popover))',
	border: '1px solid hsl(var(--border))',
	borderRadius: 8,
	fontSize: 12
};

const numberFormatter = new Intl.NumberFormat('en-GB');

// Shared across the three line charts: format the tick, never the datum, so the
// raw date survives into the tooltip (NEA-6380).
const dateAxisProps = {
	dataKey: 'date',
	tickFormatter: axisDateLabel,
	tick: { fontSize: 11, fill: 'hsl(var(--muted-foreground))' },
	tickLine: false,
	axisLine: false,
	// Keeps the labels readable whether the window is 7 days or 12 months — a
	// fixed `interval` can't do both.
	interval: 'preserveStartEnd',
	minTickGap: 28
};

/**
 * "1 Nov 2025: 1,240  +12.4%" over "2 Oct 2025: 1,103" — mirrors the NearSt
 * tooltip. Exported for its own tests, same as `ChartTooltip` in
 * `AdsPerformanceChart`: recharts never invokes a tooltip under happy-dom
 * (`ResponsiveContainer` measures 0×0), so rendering the chart can't reach it.
 */
export function ProfileViewsTooltip({ active, payload, label }) {
	if (!active || !payload?.length) return null;
	const point = payload[0]?.payload;
	if (!point) return null;

	const hasPrevious = point.prevDate != null && point.prev != null;
	const change = hasPrevious ? pointChangeLabel(point.total, point.prev) : null;

	return (
		<div className="rounded-lg border border-border bg-popover px-3 py-2 text-xs shadow-sm">
			<p className="font-semibold text-foreground">Profile views</p>
			<p className="mt-1 text-primary">
				{tooltipDateLabel(point.date ?? label)}: {numberFormatter.format(Number(point.total) || 0)}
				{change ? <span className="ml-1.5 font-semibold text-muted-foreground">{change}</span> : null}
			</p>
			{hasPrevious ? (
				<p className="text-muted-foreground">
					{tooltipDateLabel(point.prevDate)}: {numberFormatter.format(Number(point.prev) || 0)}
				</p>
			) : null}
		</div>
	);
}

/**
 * `series` already carries each day's previous-window counterpart (`prev` /
 * `prevDate`), joined on the real date by `useGbpInsights`. It used to be
 * paired here by array position, which silently mismatched the two windows
 * whenever they had different row densities.
 */
export function ProfileViewsChart({ series }) {
	return (
		<div className="mt-2 h-64 w-full">
			<ResponsiveContainer width="100%" height="100%">
				<LineChart data={series} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
					<CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
					<XAxis {...dateAxisProps} />
					<YAxis tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} tickLine={false} axisLine={false} width={32} />
					<Tooltip content={<ProfileViewsTooltip />} />
					<Line
						type="monotone"
						dataKey="prev"
						name="Previous period"
						stroke="hsl(var(--muted-foreground))"
						strokeWidth={1.5}
						strokeDasharray="4 4"
						dot={false}
					/>
					<Line type="monotone" dataKey="total" name="Profile views" stroke="hsl(var(--primary))" strokeWidth={2.5} dot={false} />
				</LineChart>
			</ResponsiveContainer>
		</div>
	);
}

export function MapsSearchChart({ series }) {
	const data = series.map((r) => ({ date: r.date, search: r.search, maps: r.maps }));
	return (
		<div className="mt-2 h-64 w-full">
			<ResponsiveContainer width="100%" height="100%">
				<LineChart data={data} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
					<CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
					<XAxis {...dateAxisProps} />
					<YAxis tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} tickLine={false} axisLine={false} width={32} />
					<Tooltip contentStyle={tooltipStyle} labelFormatter={tooltipDateLabel} />
					<Legend wrapperStyle={{ fontSize: 12, paddingTop: 4 }} iconType="circle" />
					<Line type="monotone" dataKey="search" name="Search views" stroke="hsl(var(--primary))" strokeWidth={2.5} dot={false} />
					<Line type="monotone" dataKey="maps" name="Maps views" stroke="hsl(var(--action))" strokeWidth={2.5} dot={false} />
				</LineChart>
			</ResponsiveContainer>
		</div>
	);
}

export function InteractionsBreakdownChart({ website, directions, phone }) {
	const data = [
		{ name: 'Website clicks', value: website, color: 'hsl(var(--primary))' },
		{ name: 'Directions clicks', value: directions, color: 'hsl(var(--action))' },
		{ name: 'Phone clicks', value: phone, color: 'hsl(var(--success))' }
	];
	const total = data.reduce((t, x) => t + x.value, 0);
	if (total === 0) {
		return <p className="mt-4 text-sm text-muted-foreground">No profile interactions in this period yet.</p>;
	}
	return (
		<div className="mt-2 flex items-center gap-6">
			<div className="relative h-44 w-44 shrink-0">
				<ResponsiveContainer width="100%" height="100%">
					<PieChart>
						<Pie
							data={data}
							dataKey="value"
							nameKey="name"
							innerRadius={48}
							outerRadius={78}
							paddingAngle={2}
							stroke="white"
							strokeWidth={2}
						>
							{data.map((seg) => (
								<Cell key={seg.name} fill={seg.color} />
							))}
						</Pie>
						<Tooltip contentStyle={tooltipStyle} />
					</PieChart>
				</ResponsiveContainer>
				<div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
					<div className="text-2xl font-extrabold tabular-nums">{total.toLocaleString('en-GB')}</div>
					<div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Total</div>
				</div>
			</div>
			<ul className="min-w-0 flex-1 space-y-3">
				{data.map((seg) => {
					const pct = Math.round((seg.value / total) * 100);
					return (
						<li key={seg.name} className="flex items-center gap-3">
							<span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: seg.color }} />
							<span className="flex-1 truncate text-sm font-medium text-foreground">{seg.name}</span>
							<span className="text-sm font-semibold tabular-nums">{seg.value.toLocaleString('en-GB')}</span>
							<span className="w-9 text-right text-xs tabular-nums text-muted-foreground">{pct}%</span>
						</li>
					);
				})}
			</ul>
		</div>
	);
}

export function InteractionsTimeChart({ series }) {
	const data = series.map((r) => ({
		date: r.date,
		website: r.website,
		directions: r.directions,
		phone: r.phone
	}));
	return (
		<div className="mt-2 h-64 w-full">
			<ResponsiveContainer width="100%" height="100%">
				<LineChart data={data} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
					<CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
					<XAxis {...dateAxisProps} />
					<YAxis tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} tickLine={false} axisLine={false} width={32} />
					<Tooltip contentStyle={tooltipStyle} labelFormatter={tooltipDateLabel} />
					<Legend wrapperStyle={{ fontSize: 12, paddingTop: 4 }} iconType="circle" />
					<Line type="monotone" dataKey="website" name="Website" stroke="hsl(var(--primary))" strokeWidth={2.5} dot={false} />
					<Line
						type="monotone"
						dataKey="directions"
						name="Directions"
						stroke="hsl(var(--action))"
						strokeWidth={2.5}
						dot={false}
					/>
					<Line type="monotone" dataKey="phone" name="Phone" stroke="hsl(var(--success))" strokeWidth={2.5} dot={false} />
				</LineChart>
			</ResponsiveContainer>
		</div>
	);
}
